import { useTranslation } from 'react-i18next'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FieldLabel } from './FieldLabel'
import {
  BIDDING_GOALS,
  BID_STRATEGIES,
  customBiddingGoal,
  customEventIdOfGoal,
  type BidStrategy,
  type BiddingGoalValue,
} from '@/services/lineItems/lineItemsApi'
import type { BiddingCustomEvent } from '@/hooks/useBiddingCustomEvents'

export interface BidStrategyFieldsProps {
  strategy: BidStrategy
  onStrategyChange: (strategy: BidStrategy) => void
  goal: BiddingGoalValue
  onGoalChange: (goal: BiddingGoalValue) => void
  /** Custom events the network allows as goals, offered after the standard ones. */
  customEvents?: BiddingCustomEvent[]
  /** Strategies offered — a booked marketplace product narrows these. */
  strategies?: BidStrategy[]
  disabled?: boolean
}

/**
 * The bid strategy field, plus the bidding goal that only a dynamic strategy
 * carries. Renders two `.p-field`s, so it drops into a field grid.
 */
export function BidStrategyFields({
  strategy,
  onStrategyChange,
  goal,
  onGoalChange,
  strategies = [...BID_STRATEGIES],
  customEvents = [],
  disabled,
}: BidStrategyFieldsProps) {
  const { t } = useTranslation()

  const goalLabel = (value: BiddingGoalValue) => {
    const customEventId = customEventIdOfGoal(value)
    return customEventId
      ? (customEvents.find((e) => e.id === customEventId)?.name ?? customEventId)
      : t(`lineItems.biddingGoals.${value}`)
  }

  if (!strategies.length) return null

  return (
    <>
      <div className="p-field">
        <FieldLabel tooltip={t('lineItems.bidStrategyHelp')}>
          {t('lineItems.bidStrategy')}
        </FieldLabel>
        <Select
          label={t('lineItems.bidStrategy')}
          value={strategy}
          onValueChange={(v) => onStrategyChange(v as BidStrategy)}
          disabled={disabled || strategies.length < 2}
        >
          <SelectTrigger>
            <SelectValue>{t(`lineItems.bidStrategies.${strategy}`)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {strategies.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`lineItems.bidStrategies.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* The goal only drives a dynamic strategy, so it hides for the others. */}
      {strategy === 'DYNAMIC' && (
        <div className="p-field">
          <FieldLabel tooltip={t('lineItems.biddingGoalHelp')}>
            {t('lineItems.biddingGoal')}
          </FieldLabel>
          <Select
            label={t('lineItems.biddingGoal')}
            value={goal}
            onValueChange={(v) => onGoalChange(v as BiddingGoalValue)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue>{goalLabel(goal)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {BIDDING_GOALS.map((g) => (
                <SelectItem key={g} value={g}>
                  {t(`lineItems.biddingGoals.${g}`)}
                </SelectItem>
              ))}
              {customEvents.map((e) => (
                <SelectItem key={e.id} value={customBiddingGoal(e.id)}>
                  {e.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </>
  )
}
